// src/components/LanguageSwitch.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../utils/axiosInstance";
import { isLoggedIn } from "../utils/auth";
import "./LanguageSwitch.css";

const LANG_KEY = "ui_lang";
const LANGS = ["tr", "en", "de"];

// Dil değişikliğini sunucuya kalıcı olarak kaydeder — bu değer, dil bazlı
// ödeme fiyatlandırmasının (BuyCreditsPage) TEK güvenilir kaynağıdır.
// İstemci taraflı localStorage değeri ödeme tutarını belirlemek için asla
// kullanılmaz (bkz. backend payment/views.py create_payment_order).
function syncLanguageToServer(lang) {
  if (!isLoggedIn()) return;
  axiosInstance.post("/api/auth/language/", { language: lang }).catch(() => {});
}

export default function LanguageSwitch({ collapsed = false }) {
  const { t, i18n } = useTranslation();
  const [lang, setLang] = useState(localStorage.getItem(LANG_KEY) || "tr");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    // sayfa açılınca i18n'i localStorage ile senkron tut
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    // Giriş yapılmış bir oturumda, sunucudaki kayıtlı tercih ile
    // localStorage'daki mevcut dil arasında sapma olmasın diye senkronize et.
    syncLanguageToServer(lang);

    // Kullanıcı bu bileşen zaten ekrandayken login olursa (sayfa yenilenmeden)
    // dil tercihini o an sunucuya senkronize et — aksi halde server tarafında
    // varsayılan "tr" kalabilir. localStorage'dan taze okunur (stale closure
    // olmasın diye state yerine).
    const onAuthChange = () => syncLanguageToServer(localStorage.getItem(LANG_KEY) || "tr");
    window.addEventListener("auth:change", onAuthChange);
    return () => window.removeEventListener("auth:change", onAuthChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Collapsed popover: dışarı tıklanınca kapat
  useEffect(() => {
    if (!popoverOpen) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [popoverOpen]);

  const setLanguage = (next) => {
    setLang(next);
    localStorage.setItem(LANG_KEY, next);

    // i18n UI çeviri
    i18n.changeLanguage(next);
    syncLanguageToServer(next);

    // ✅ backend istekleri refetch tetiklensin
    window.dispatchEvent(new Event("lang:change"));
    setPopoverOpen(false);
  };

  if (collapsed) {
    const otherLangs = LANGS.filter((code) => code !== lang);

    // Basit popover konumlandırma: sidebar'ın collapsed halde padding'i çok
    // dar olduğu için yüzdesel (left:100%) konumlandırma popover'ı sidebar
    // sınırının içinde bırakıyordu. Bunun yerine tetikleyici butonun
    // GERÇEK ekran konumu (getBoundingClientRect) okunup fixed pozisyon
    // olarak veriliyor — sidebar'ın kendi genişliği/padding'i değişse bile
    // doğru yerde açılır.
    const togglePopover = () => {
      if (!popoverOpen && triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setPopoverPos({ top: r.top, left: r.right + 8 });
      }
      setPopoverOpen((v) => !v);
    };

    return (
      <div className="lang-switch lang-switch--collapsed" ref={wrapperRef}>
        <button
          type="button"
          ref={triggerRef}
          className="lang-collapsed-trigger"
          onClick={togglePopover}
          aria-label={t("languageSwitch.currentLangAria", { lang: lang.toUpperCase() })}
          aria-haspopup="menu"
          aria-expanded={popoverOpen}
        >
          {lang.toUpperCase()}
        </button>
        {popoverOpen && (
          <div
            className="lang-collapsed-popover"
            role="menu"
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            {otherLangs.map((code) => (
              <button
                key={code}
                type="button"
                role="menuitem"
                className="lang-collapsed-option"
                onClick={() => setLanguage(code)}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="lang-switch lang-switch--pill" role="group" aria-label={t("languageSwitch.ariaLabel")}>
      {LANGS.map((code) => (
        <button
          key={code}
          type="button"
          className={`lang-pill-btn ${lang === code ? "active" : ""}`}
          onClick={() => setLanguage(code)}
          aria-pressed={lang === code}
          aria-label={code}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
